import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
	generateCssStyling,
	generatePdfTronStyling,
} from '@core/util/documents';
import { createId } from '@core/util/file';
import { WordExportService } from '@functions/word-export.service';
import {
	DocumentStyle,
	DocumentStyleAttributes,
	DocumentStyleAttributesMapping,
	DocumentStyleElementTags,
	DocumentStyleElementTagsMapping,
	DocumentStylingElement,
	DocumentStylingInputTypes,
} from '@models/businessSettings/documentStyles';
import { DocumentMargins } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { DocumentStylingDispatch } from '@state/document-styling/document-styling.dispatch';
import { DocumentStylingState } from '@state/document-styling/document-styling.state';
import { cloneDeep, isEqual } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';

@UntilDestroy()
@Component({
	selector: 'app-business-document-styling',
	templateUrl: './document-styling.component.html',
	styleUrls: ['./document-styling.component.scss'],
	standalone: false,
})
export class DocumentStylingComponent implements OnInit {
	@SelectSnapshot(DocumentStylingState.getDefaultEditableStylingElements)
	defaultElements: DocumentStylingElement[];
	@Select(DocumentStylingState.getStyles) mergedStyles$: Observable<
		DocumentStylingElement[]
	>;
	@SelectSnapshot(DocumentStylingState.getPdfTronStyling) getPdfTronStyling;
	@SelectSnapshot(BusinessState.getDefaultPageMargins) margins: DocumentMargins;

	onUpdateStyling = new Subject<{ subscriptionId: string; url: string }>();

	public icons = ICON;
	public url = '';
	public elements: DocumentStylingElement[] = [];

	private baseElementsReference: DocumentStylingElement[] = [];

	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	public customCssFromStyling: string;

	public html = `
		<h1>HEADER 1 </h1>
		<p>
		Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
		</p>
		<h2>HEADER 2 </h2>
		<p>
		Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Urna et pharetra pharetra massa massa ultricies mi quis hendrerit. In nisl nisi scelerisque eu. Nulla aliquet enim tortor at auctor urna nunc id.
		</p>
		<h3>HEADER 3 </h3>
		<p>
		Leo vel orci porta non. Aliquam malesuada bibendum arcu vitae elementum curabitur vitae nunc. Diam quam nulla porttitor massa id neque aliquam. Dictum sit amet justo donec enim diam vulputate ut pharetra. Platea dictumst vestibulum rhoncus est pellentesque elit ullamcorper dignissim.
		</p>
		<h4>HEADER 4 </h4>
		<p>
		Enim sit amet venenatis urna cursus eget nunc. Adipiscing elit ut aliquam purus sit amet luctus venenatis. Aliquam faucibus purus in massa tempor nec feugiat.
		</p>
		<h5>HEADER 5 </h5>
		<p>
		Nec sagittis aliquam malesuada bibendum arcu. Tempus iaculis urna id volutpat lacus laoreet non curabitur. Ante in nibh mauris cursus mattis molestie. Erat velit scelerisque in dictum non consectetur a.
		</p>
		<h6>HEADER 6 </h6>
		<p>
		Dui nunc mattis enim ut. Purus semper eget duis at tellus. Eu mi bibendum neque egestas congue quisque egestas diam. Rhoncus mattis rhoncus urna neque viverra. Venenatis cras sed felis eget. Nibh nisl condimentum id venenatis a condimentum vitae sapien pellentesque.
		</p>
		<ul>
			<li>List item 1 </li>
			<li>List item 2 </li>
			<li>List item 3 </li>
			<ul>
				<li>Sub list item 1</li>
				<li>Sub list item 2</li>
			</ul>
		</ul>
		<br/>
		<br/>
		<br/>
		<br/>
		<br/>
		<br/>
		<br/>
		<h4>Table</h4>
		<table class="table_styling_preview" id="table_styling_preview">
			<tr>
				<td>In est ante in nibh. Cras semper auctor neque vitae tempus quam pellentesque nec.</td>
				<td>Pellentesque eu tincidunt tortor aliquam nulla facilisi cras fermentum.</td>
			</tr>
			<tr>
				<td>Donec ultrices tincidunt arcu non sodales neque sodales ut.</td>
				<td>Eget lorem dolor sed viverra ipsum.</td>
			</tr>
		</table>

	`;

	public elementTagsMapping = DocumentStyleElementTagsMapping;
	public elementAttributesMapping = DocumentStyleAttributesMapping;
	public documentStylingInputTypes = DocumentStylingInputTypes;
	public lastSubscriptionId;
	public disableSave = true;
	public showWordPreview = false;
	public loading = false;
	public options = [
		{ label: 'PDF preview', value: 'PDF' },
		// { label: 'Word preview', value: 'WORD' }, //Disable for now since we don't have any word export only options
	];

	public currentState: 'PDF' | 'WORD' = 'PDF';
	private debounceSubject = new Subject<DocumentStylingElement[]>();
	constructor(
		private snackbarService: SnackbarService,
		private documentStylingDispatch: DocumentStylingDispatch,
		private wordExportService: WordExportService,
	) {}

	ngOnInit() {
		this.onUpdateStyling.pipe(untilDestroyed(this)).subscribe((res) => {
			//Prevents race condition created by waiting for cloudconvert response
			if (
				this.lastSubscriptionId === res.subscriptionId ||
				!this.lastSubscriptionId
			) {
				this.loading = false;
				this.url = res.url;
			}
		});

		// Prevents spamming the word export conversion;
		this.debounceSubject
			.pipe(debounceTime(2000))
			.subscribe(async (tmpElements) => {
				const css = generateCssStyling(tmpElements);
				this.customCssFromStyling = generateCssStyling(tmpElements);
				const tmpId = createId();
				this.lastSubscriptionId = tmpId;

				if (this.showWordPreview) {
					this.loading = true;
					this.wordExportService
						.exportToWord('Preview', 'Preview', this.html, this.margins, css)
						.pipe(take(1))
						.subscribe((res) => {
							this.onUpdateStyling.next({ subscriptionId: tmpId, url: res });
						});
				}
			});

		this.mergedStyles$
			.pipe(distinctUntilChanged((pre, curr) => isEqual(pre, curr)))
			.subscribe((elements) => {
				this.elements = cloneDeep(elements);
				this.baseElementsReference = cloneDeep(elements);
				this.updateStyling();
			});
	}

	save(form: NgForm) {
		if (form.valid) {
			this.defaultElements.forEach((defaultElement) => {
				const element = this.elements.find(
					(element) => defaultElement.tag === element.tag,
				);

				if (!isEqual(element, defaultElement)) {
					// Save to DB
					const { tag, styles, id } = cloneDeep(element);
					const tmpStyles = styles.map(
						({ type, value }): DocumentStyle => ({ type, value }),
					);
					if (id) {
						this.documentStylingDispatch.UpdateStyle(id, {
							tag,
							styles: tmpStyles,
						});
					} else {
						this.documentStylingDispatch.AddStyle({ tag, styles: tmpStyles });
					}
				}
			});

			this.snackbarService.openSnackBar({
				text: 'Document styling saved',
				type: 'success',
			});
		}
	}

	async updateStyling() {
		this.disableSave = isEqual(this.elements, this.baseElementsReference);

		this.debounceSubject.next(this.elements);
	}

	onDropdownSelect(
		result: DropdownResult<DropdownOption<string>>,
		tag: DocumentStyleElementTags,
		styleType: DocumentStyleAttributes,
	) {
		const style = this.elements
			.find((element) => element.tag === tag)
			.styles.find((style) => style.type === styleType);
		style.value = result.result.value;

		this.updateStyling();
	}

	switchPreview(state) {
		this.currentState = state;
		if (!this.showWordPreview) {
			this.loading = true;
			this.updateStyling();
		}
		this.showWordPreview = !this.showWordPreview;
	}
}
